import type { CSSProperties, MutableRefObject, ReactNode } from 'react';
import type { Element, GroupElement as GroupElementType, ImageElement as ImageElementType } from '@/components/canvas/document/publicTypes';
import type { WorkspaceInteractionApi } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';
import type { MarqueeRect } from '@/components/canvas/workspace/controllers/marqueeSelectUtils';
import { WorldMarqueeLayer } from '@/components/canvas/workspace/overlays/WorldMarqueeOverlay';
import { WorldSelectionOverlay } from '@/components/canvas/workspace/overlays/WorldSelectionOverlay';

export interface WorkspaceStageProps {
  stageHostRef: MutableRefObject<HTMLDivElement | null>;
  stageRef: MutableRefObject<HTMLDivElement | null>;
  stageStyle: CSSProperties;
  zoom: number;
  interaction: WorkspaceInteractionApi;
  onStageMouseDown: (e: React.MouseEvent) => void;
  onStageMouseMove: (e: React.MouseEvent) => void;
  onStageBackgroundClick: (e: React.MouseEvent) => void;
  worldMarquee?: MarqueeRect | null;
  children: ReactNode;
}

export function WorkspaceStage({
  stageHostRef,
  stageRef,
  stageStyle,
  zoom,
  interaction,
  onStageMouseDown,
  onStageMouseMove,
  onStageBackgroundClick,
  worldMarquee = null,
  children,
}: WorkspaceStageProps) {
  const {
    elements,
    rootElements,
    visibleElements,
    editingGroupId,
    hoveredElementId,
    setHoveredElementId,
    hoveredBlockId,
    setHoveredBlockId,
    pageBlocks = [],
    baseCursor,
    editingImagePosition,
    updateElement,
    handleMouseUpCombined,
    workspaceElements,
  } = interaction;

  return (
    <div
      ref={stageHostRef}
      className="absolute inset-0 overflow-hidden outline-none"
      onMouseDown={(e) => {
        (e.currentTarget as HTMLElement).focus();
        if (interaction.isPresentationMode) return;
        onStageMouseDown(e);
      }}
      onMouseMove={(e) => {
        if (interaction.isPresentationMode) {
          (e.currentTarget as HTMLDivElement).style.cursor = 'default';
          return;
        }
        if (editingImagePosition) {
          const el = elements.find((elem) => elem.id === editingImagePosition.id) as
            | ImageElementType
            | undefined;
          if (el && el.type === 'image') {
            const pageDx = (e.clientX - editingImagePosition.startClientX) / zoom;
            const pageDy = (e.clientY - editingImagePosition.startClientY) / zoom;
            const percentDx = (pageDx / el.size.width) * 100;
            const percentDy = (pageDy / el.size.height) * 100;
            const newX = Math.min(
              100,
              Math.max(0, editingImagePosition.startObjectPositionX + percentDx),
            );
            const newY = Math.min(
              100,
              Math.max(0, editingImagePosition.startObjectPositionY + percentDy),
            );
            updateElement(el.id, {
              data: { objectPosition: { x: newX, y: newY } },
            });
          }
        } else {
          onStageMouseMove(e);
        }
        const target = e.target as HTMLElement;
        const handleEl = target.closest('[data-handle-cursor]') as HTMLElement | null;
        let nextCursor = baseCursor;
        let nextHoveredId: string | null = null;
        if (handleEl) {
          nextCursor = handleEl.getAttribute('data-handle-cursor') || baseCursor;
        } else {
          const blockHit = target.closest('[data-block-hit]') as HTMLElement | null;
          const blockHitId = blockHit?.getAttribute('data-block-id') ?? null;
          if (blockHitId && pageBlocks.some((b) => b.id === blockHitId)) {
            if (blockHitId !== hoveredBlockId) setHoveredBlockId(blockHitId);
            if (hoveredElementId !== null) setHoveredElementId(null);
            nextCursor = 'move';
          } else {
            if (hoveredBlockId !== null) setHoveredBlockId(null);
          const hitEl = target.closest('[data-element-hit]') as HTMLElement | null;
          const hitId = hitEl?.getAttribute('data-element-id') ?? null;
          if (hitId) {
            const isPageRoot = rootElements.some((el) => el.id === hitId);
            const isWsRoot = workspaceElements.some(
              (el) => el.id === hitId && !el.layout.parentId,
            );
            const grp = elements.find((g) => g.id === editingGroupId) as GroupElementType | undefined;
            const isEditingChild = !!editingGroupId && grp?.data.children.includes(hitId);
            if (isPageRoot || isEditingChild) {
              const hitEl2 = visibleElements.find((el) => el.id === hitId);
              if (!hitEl2?.interaction.locked) {
                nextHoveredId = hitId;
                if (hitEl2?.type !== 'text') nextCursor = 'move';
              }
            } else if (isWsRoot) {
              const wsEl = workspaceElements.find((el) => el.id === hitId);
              if (wsEl && !wsEl.interaction.locked) {
                nextHoveredId = hitId;
                if (wsEl.type !== 'text') nextCursor = 'move';
              }
            }
          }
          }
        }
        if (nextHoveredId !== hoveredElementId) setHoveredElementId(nextHoveredId);
        const host = e.currentTarget as HTMLDivElement;
        if (host.style.cursor !== nextCursor) host.style.cursor = nextCursor;
      }}
      onMouseUp={handleMouseUpCombined}
      onMouseLeave={(e) => {
        if (hoveredElementId !== null) setHoveredElementId(null);
        if (hoveredBlockId !== null) setHoveredBlockId(null);
        (e.currentTarget as HTMLDivElement).style.cursor = baseCursor;
        handleMouseUpCombined(e);
      }}
      onClick={interaction.isPresentationMode ? undefined : onStageBackgroundClick}
      onContextMenuCapture={interaction.isPresentationMode ? undefined : interaction.handleContextMenu}
      tabIndex={0}
    >
      <div
        ref={stageRef}
        data-workspace-stage
        className="absolute left-0 top-0 z-[2] box-border overflow-visible"
        style={stageStyle}
        data-workspace-dragging={interaction.elementDrag ? '' : undefined}
      >
        <WorldMarqueeLayer marquee={worldMarquee} zoom={zoom} />
        {children}
        {interaction.useWorldSelectionChrome && !interaction.elementDrag && !interaction.hideSelectionChrome && (
          <WorldSelectionOverlay
            pages={interaction.pages}
            workspaceElements={interaction.workspaceElements}
            selectedElementIds={interaction.selectedElementIds}
            selectedPageIds={interaction.selectedPageIds}
            currentPageId={interaction.currentPageId}
            zoom={zoom}
            elementDrag={!!interaction.elementDrag}
            dragOffset={interaction.dragOffset}
            onStartUnionResize={interaction.startUnionResize}
          />
        )}
      </div>
    </div>
  );
}
